import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatLojaName } from "../utils/lodgeName";
import {
  Compass,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  UserCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public Check-In page reached by scanning the QR code from a Session.
 * URL format: /checkin?s=<session_id>&t=<token>
 * The user selects their own name to register presence.
 */
export const CheckinPublicPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("s");
  const token = params.get("t");

  const [sessionInfo, setSessionInfo] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [status, setStatus] = useState("loading"); // loading | ready | sending | success | error
  const [lojaName, setLojaName] = useState("Loja Maçônica");
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!sessionId || !token) {
        setStatus("error");
        setStatusMsg("Link de check-in inválido.");
        return;
      }
      try {
        const [tokRes, membRes, lojaRes] = await Promise.all([
          axios.get(`${API}/sessions/${sessionId}/qr-token`),
          axios.get(`${API}/members`),
          axios.get(`${API}/lojas/current`).catch(() => ({ data: null })),
        ]);
        setSessionInfo(tokRes.data);
        setMembers(membRes.data);
        if (lojaRes.data) {
          const l = lojaRes.data;
          setLojaName(formatLojaName(l, { withArls: true }));
        }
        setStatus("ready");
      } catch (err) {
        setStatus("error");
        setStatusMsg("Não foi possível carregar a sessão. QR expirado ou removido.");
      }
    };
    load();
  }, [sessionId, token]);

  const handleCheckin = async () => {
    if (!selectedMemberId) return;
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/sessions/${sessionId}/qr-checkin`, {
        member_id: selectedMemberId,
        token,
      });
      setStatus("success");
      setStatusMsg(res.data.message);
    } catch (err) {
      setStatus("error");
      setStatusMsg(err.response?.data?.detail || "Erro ao registrar presença.");
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14] flex items-center justify-center p-6 font-sans" data-testid="checkin-page">
      <div className="w-full max-w-md bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-6 bg-[#080D18] border-b border-[#D4AF37]/20 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-3">
            <Compass className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] font-bold">
            À Glória do G∴A∴D∴U∴
          </div>
          <h1 className="text-xl font-serif font-bold text-white mt-1">Check-in de Presença</h1>
          <p className="text-[11px] text-slate-400 mt-1">{lojaName || "Loja Maçônica"}</p>
        </div>

        <div className="p-6 space-y-5">
          {status === "loading" && (
            <div className="flex flex-col items-center py-10 text-slate-400 text-sm">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              <span>Validando QR Code...</span>
            </div>
          )}

          {status === "error" && (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-rose-300 font-semibold">Não foi possível registrar</p>
                <p className="text-xs text-rose-200 mt-1">{statusMsg}</p>
              </div>
            </div>
          )}

          {status === "success" && (
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-200 font-bold">Presença registrada!</p>
                <p className="text-xs text-emerald-300 mt-1">{statusMsg}</p>
                <p className="text-[10px] text-slate-500 mt-3 uppercase tracking-wider">
                  Bons trabalhos, meu Ir∴!
                </p>
              </div>
            </div>
          )}

          {(status === "ready" || status === "sending") && sessionInfo && (
            <>
              <div className="bg-[#131C2E] rounded-xl p-4 border border-slate-700/60">
                <div className="text-[10px] uppercase text-slate-400 tracking-wider">Sessão</div>
                <div className="text-lg font-serif font-bold text-white mt-0.5">
                  nº {sessionInfo.session_number}
                </div>
                <div className="text-xs text-slate-300 mt-1">{sessionInfo.title}</div>
                <div className="text-[11px] text-[#D4AF37] mt-1 font-mono">{sessionInfo.date}</div>
              </div>

              <div>
                <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1.5">
                  <UserCheck className="w-3 h-3" />
                  <span>Identifique-se</span>
                </label>
                <select
                  data-testid="checkin-select-member"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  disabled={status === "sending"}
                  className="mt-2 w-full bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-3 text-sm text-white focus:border-[#D4AF37] outline-none"
                >
                  <option value="">Selecione seu nome</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} — CIM {m.cim}
                    </option>
                  ))}
                </select>
              </div>

              <button
                data-testid="checkin-btn-submit"
                onClick={handleCheckin}
                disabled={!selectedMemberId || status === "sending"}
                className="w-full flex items-center justify-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed text-slate-950 font-bold py-3 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)]"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Registrando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Registrar minha presença</span>
                  </>
                )}
              </button>

              <p className="text-[10px] text-slate-500 text-center uppercase tracking-wider">
                O check-in é registrado apenas com o QR ativo no Templo
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
